import { Card } from '../../components/ui/Card';
import { reportRejectReason, syncConflictReason } from '../data/model';
import type { DemoSnapshot, FlowState, ReportStatus, SyncItem, WorkOrder } from '../data/types';
import { getPhoneFrameStatusText, getPhoneStatus, getSubmitButtonLabel, isSubmitDisabled } from '../utils';

type MobileView = 'today' | 'job' | 'voice' | 'submit';

const MOBILE_STEPS: Array<{ id: MobileView; label: string }> = [
  { id: 'today', label: 'Planning' },
  { id: 'job', label: 'Détail' },
  { id: 'voice', label: 'Vocal' },
  { id: 'submit', label: 'Envoi' },
];

export function MobilePage(props: {
  iconSrc: string;
  activeOrder: WorkOrder | null;
  currentMobileView: MobileView;
  flowState: FlowState;
  mobileToday: DemoSnapshot['mobileToday'];
  syncItems: SyncItem[];
  onSelectView: (view: MobileView) => void;
  onStartJob: () => Promise<void>;
  onCapturePhotos: () => Promise<void>;
  onOpenVoice: () => void;
  onFinalizeVoice: () => Promise<void>;
  onCaptureSignature: () => Promise<void>;
  onRetrySync: () => Promise<void>;
  onSubmitReport: () => Promise<void>;
  onOpenReworkMobile: () => void;
  canOperateMobile: boolean;
}) {
  const phoneStatus = getPhoneStatus(props.flowState);

  return (
    <div className="page-stack">
      <div className="mobile-demo">
        <Card title="Parcours mobile" subtitle="Basculer d'un écran à l'autre pour suivre l'intervention.">
          <div className="mobile-tabs">
            {MOBILE_STEPS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`mobile-tabs__button${item.id === props.currentMobileView ? ' is-active' : ''}`}
                onClick={() => props.onSelectView(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <ul className="check-list">
            <li>Mode hors ligne prioritaire pour planning, notes, photos, signature et brouillon vocal.</li>
            <li>Synchronisation visible, pas silencieuse.</li>
            <li>Date locale du technicien conservée pour le planning et les preuves.</li>
          </ul>
          <div className="card-divider" />
          <div className="stack-list">
            {props.syncItems.map((item) => (
              <div key={`${item.title}-${item.meta}`} className={`sync-item${item.status ? ` sync-item--${item.status}` : ''}`}>
                <strong>{item.title}</strong>
                <span>{item.detail}</span>
                <div className="sync-item__meta">
                  <span>{item.meta}</span>
                  <span>
                    {item.status === 'conflict'
                      ? 'Blocage'
                      : item.status === 'success'
                        ? 'Aligné'
                        : item.status === 'warning'
                          ? 'Reprise'
                          : 'Synchronisation prioritaire'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="phone-frame">
          <div className="phone-frame__status">
            <span>10:16</span>
            <span>{getPhoneFrameStatusText(props.flowState)}</span>
          </div>

          <div className="phone-screen">
            {props.currentMobileView === 'today' ? (
              <MobileTodayScreen
                iconSrc={props.iconSrc}
                activeOrder={props.activeOrder}
                phoneStatus={phoneStatus}
                reportStatus={props.flowState.reportStatus}
                mobileToday={props.mobileToday}
                onStartJob={props.onStartJob}
                canOperateMobile={props.canOperateMobile}
              />
            ) : null}
            {props.currentMobileView === 'job' ? (
              <MobileJobScreen
                activeOrder={props.activeOrder}
                flowState={props.flowState}
                onStartJob={props.onStartJob}
                onCapturePhotos={props.onCapturePhotos}
                onOpenVoice={props.onOpenVoice}
                onCaptureSignature={props.onCaptureSignature}
                canOperateMobile={props.canOperateMobile}
              />
            ) : null}
            {props.currentMobileView === 'voice' ? (
              <MobileVoiceScreen
                activeOrder={props.activeOrder}
                flowState={props.flowState}
                onFinalizeVoice={props.onFinalizeVoice}
                canOperateMobile={props.canOperateMobile}
              />
            ) : null}
            {props.currentMobileView === 'submit' ? (
              <MobileSubmitScreen
                activeOrder={props.activeOrder}
                flowState={props.flowState}
                onRetrySync={props.onRetrySync}
                onSubmitReport={props.onSubmitReport}
                onOpenReworkMobile={props.onOpenReworkMobile}
                canOperateMobile={props.canOperateMobile}
              />
            ) : null}
          </div>

          <div className="phone-frame__nav">
            {MOBILE_STEPS.map((item) => (
              <span key={item.id} className={item.id === props.currentMobileView ? 'is-active' : ''}>
                {item.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileTodayScreen(props: {
  iconSrc: string;
  activeOrder: WorkOrder | null;
  phoneStatus: { label: string; className: string };
  reportStatus: ReportStatus;
  mobileToday: DemoSnapshot['mobileToday'];
  onStartJob: () => Promise<void>;
  canOperateMobile: boolean;
}) {
  return (
    <>
      <div className="phone-card phone-card--hero">
        <div className="phone-card__header">
          <span className="phone-brand">
            <img src={props.iconSrc} alt="" />
            TS
          </span>
          <span className={props.phoneStatus.className}>{props.phoneStatus.label}</span>
        </div>
        <div className="eyebrow eyebrow--hero">Prochaine intervention</div>
        <strong>{props.activeOrder?.title ?? 'Diagnostic PAC'}</strong>
        <span>{props.activeOrder?.client ?? 'Clinique Valmy'} · Départ {props.activeOrder?.window ?? '11:15'}</span>
        <button
          type="button"
          className="primary-button primary-button--light"
          onClick={() => void props.onStartJob()}
          disabled={!props.canOperateMobile}
        >
          {props.reportStatus === 'rejected' ? 'Reprendre le dossier' : 'Démarrer'}
        </button>
      </div>

      <div className="phone-list">
        {props.mobileToday.map((item) => (
          <div key={`${item.time}-${item.label}`} className="phone-list__item">
            <div>
              <strong>{item.label}</strong>
              <span>{item.place}</span>
            </div>
            <em>{item.time}</em>
          </div>
        ))}
      </div>
    </>
  );
}

function MobileJobScreen(props: {
  activeOrder: WorkOrder | null;
  flowState: FlowState;
  onStartJob: () => Promise<void>;
  onCapturePhotos: () => Promise<void>;
  onOpenVoice: () => void;
  onCaptureSignature: () => Promise<void>;
  canOperateMobile: boolean;
}) {
  return (
    <>
      <div className="phone-card">
        <div className="phone-card__header">
          <strong>Détail intervention</strong>
          <span className={`status-pill ${props.flowState.reportStatus === 'rejected' ? 'status-pill--warning' : 'status-pill--info'}`}>
            {props.flowState.reportStatus === 'rejected' ? 'Reprise' : 'Prêt'}
          </span>
        </div>
        <span>{props.activeOrder?.client ?? 'Clinique Valmy'} · {props.activeOrder?.site ?? '4 avenue Pasteur, Dijon'}</span>
      </div>

      {props.flowState.reportStatus === 'rejected' ? (
        <div className="alert-card alert-card--warning">
          <strong>Correction demandée</strong>
          <span>{props.flowState.rejectionReason ?? reportRejectReason}</span>
        </div>
      ) : null}

      <div className="phone-card">
        <span className="phone-card__label">Progression checklist</span>
        <div className="progress-bar">
          <span style={{ width: '72%' }} />
        </div>
        <ul className="check-list">
          <li>Historique incidents PAC chargé</li>
          <li>Kit nettoyage disponible</li>
          <li>{props.flowState.signatureCaptured ? 'Signature client capturée' : 'Signature client à récupérer'}</li>
        </ul>
      </div>

      <div className="phone-actions">
        <button type="button" onClick={() => void props.onStartJob()} disabled={!props.canOperateMobile}>
          Début
        </button>
        <button type="button" onClick={() => void props.onCapturePhotos()} disabled={!props.canOperateMobile}>
          Photos
        </button>
        <button type="button" onClick={props.onOpenVoice} disabled={!props.canOperateMobile}>
          Vocal
        </button>
        <button type="button" onClick={() => void props.onCaptureSignature()} disabled={!props.canOperateMobile}>
          Signature
        </button>
      </div>
    </>
  );
}

function MobileVoiceScreen(props: {
  activeOrder: WorkOrder | null;
  flowState: FlowState;
  onFinalizeVoice: () => Promise<void>;
  canOperateMobile: boolean;
}) {
  return (
    <>
      <div className="phone-card">
        <div className="phone-card__header">
          <strong>Rapport vocal</strong>
          <span className="status-pill status-pill--review">{props.flowState.voiceCaptured ? 'Capturé' : 'Brouillon'}</span>
        </div>
        <span>
          {props.activeOrder ? `${props.activeOrder.id} · ` : ''}
          {props.flowState.reportStatus === 'rejected' ? 'Version corrigée à préparer' : 'Brouillon stocké localement'}
        </span>
      </div>

      <div className="wave-card">
        <div className="wave-card__bar" />
        <button type="button" className="record-button" onClick={() => void props.onFinalizeVoice()} disabled={!props.canOperateMobile}>
          Arrêter
        </button>
      </div>

      <div className="phone-card">
        <span className="phone-card__label">Aperçu structuré</span>
        <p>Diagnostic PAC confirmé. Nettoyage condenseur effectué. Contrôle sonde extérieure recommandé avant remise en service complète.</p>
      </div>
    </>
  );
}

function MobileSubmitScreen(props: {
  activeOrder: WorkOrder | null;
  flowState: FlowState;
  onRetrySync: () => Promise<void>;
  onSubmitReport: () => Promise<void>;
  onOpenReworkMobile: () => void;
  canOperateMobile: boolean;
}) {
  const submitProofTags = [
    props.flowState.photosCaptured ? '2 photos PAC' : 'Photos à reprendre',
    props.flowState.signatureCaptured ? 'Signature client' : 'Signature manquante',
    props.flowState.voiceCaptured ? 'Rapport vocal' : 'Vocal à finaliser',
  ];

  let syncStatusText = 'Données locales · Synchronisation en attente';
  if (props.flowState.syncStatus === 'conflict') syncStatusText = 'Conflit de synchronisation détecté';
  if (props.flowState.syncStatus === 'retrying') syncStatusText = 'Relance de synchronisation en cours';
  if (props.flowState.syncStatus === 'synced') syncStatusText = 'Données alignées avec le serveur';

  return (
    <>
      <div className="phone-card">
        <div className="phone-card__header">
          <strong>Relecture et envoi</strong>
          <span className={`status-pill ${props.flowState.syncStatus === 'conflict' ? 'status-pill--danger' : 'status-pill--ready'}`}>
            {props.flowState.syncStatus === 'conflict' ? 'Synchronisation bloquée' : 'File de synchronisation'}
          </span>
        </div>
        <span>
          {props.flowState.reportStatus === 'rejected'
            ? "L'exploitation demande une correction avant facturation."
            : 'Toutes les preuves terrain sont pilotées ici.'}
        </span>
      </div>

      {props.flowState.syncStatus === 'conflict' ? (
        <div className="alert-card alert-card--danger">
          <strong>Conflit de synchronisation</strong>
          <span>{props.flowState.syncConflictReason ?? syncConflictReason}</span>
          <div className="action-row">
            <button
              type="button"
              className="secondary-button"
              onClick={() => void props.onRetrySync()}
              disabled={!props.canOperateMobile}
            >
              Relancer la synchronisation
            </button>
          </div>
        </div>
      ) : null}

      {props.flowState.reportStatus === 'rejected' ? (
        <div className="alert-card alert-card--warning">
          <strong>Validation refusée</strong>
          <span>{props.flowState.rejectionReason ?? reportRejectReason}</span>
          <div className="action-row">
            <button type="button" className="secondary-button" onClick={props.onOpenReworkMobile} disabled={!props.canOperateMobile}>
              Corriger le rapport
            </button>
          </div>
        </div>
      ) : null}

            {props.flowState.reportStatus === 'submitted' ? (
        <div className="alert-card alert-card--info">
          <strong>Rapport envoyé</strong>
          <span>En attente de relecture exploitation.</span>
        </div>
      ) : null}

      {props.flowState.reportStatus === 'resubmitted' ? (
        <div className="alert-card alert-card--info">
          <strong>Rapport re-soumis</strong>
          <span>L'exploitation relit la version corrigée.</span>
        </div>
      ) : null}

      {props.flowState.syncStatus === 'synced' &&
      props.flowState.reportStatus !== 'submitted' &&
      props.flowState.reportStatus !== 'resubmitted' &&
      props.flowState.reportStatus !== 'rejected' ? (
        <div className="alert-card alert-card--success">
          <strong>Synchronisation alignée</strong>
          <span>Le rapport peut être envoyé à l'exploitation.</span>
        </div>
      ) : null}

      <div className="proof-tags">
        {submitProofTags.map((item) => (
          <span key={item} className="proof-tag">
            {item}
          </span>
        ))}
      </div>

      <div className="phone-card">
        <span className="phone-card__label">État de synchronisation</span>
        <p>{props.activeOrder ? `${props.activeOrder.id} · ${syncStatusText}` : syncStatusText}</p>
      </div>

      <button
        type="button"
        className="primary-button primary-button--block"
        onClick={() => void props.onSubmitReport()}
        disabled={!props.canOperateMobile || isSubmitDisabled(props.flowState)}
      >
        {getSubmitButtonLabel(props.flowState)}
      </button>
    </>
  );
}
